import { useApiQuery } from '../useApiQuery';
import { useHasPermission } from '@/hooks/usePermissions';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { apiGet, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { estimateKeys } from '@/lib/queryKeys';
import type { EstimateListResponse, EstimateStatus } from '@/types/estimate';
import { type DateFilter } from './useDashboardStats';
import { dateFilterToApiRange } from './dashboardRange';

export interface PipelineData {
  pipelineValue: number;
  workOrders: number;
  awaitingApproval: number;
  totalLeads: number;
  totalEstimates: number;
}

export interface FinancialData {
  outstanding: number;
  overdue: number;
  collectedToday: number;
}

export interface PipelineStats {
  pipeline: PipelineData;
  financial: FinancialData;
}

const EMPTY: PipelineStats = {
  pipeline: {
    pipelineValue: 0,
    workOrders: 0,
    awaitingApproval: 0,
    totalLeads: 0,
    totalEstimates: 0,
  },
  financial: { outstanding: 0, overdue: 0, collectedToday: 0 },
};

const AWAITING_APPROVAL_STATUSES: EstimateStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP', 'REVISED'];

export async function fetchPipelineStats(
  filter: DateFilter,
  referenceDate = new Date(),
  timeZone?: string,
): Promise<PipelineStats> {
  const range = dateFilterToApiRange(filter, referenceDate, timeZone);
  const params = { ...range, page: 1, limit: 1 };
  const [all, ...pending] = await Promise.all([
    apiGet<EstimateListResponse>(operationsClient, opsEstimateEndpoints.list(), {
      params,
    }),
    ...AWAITING_APPROVAL_STATUSES.map((status) =>
      apiGet<EstimateListResponse>(operationsClient, opsEstimateEndpoints.list(), {
        params: { ...params, status },
      }),
    ),
  ]);
  return {
    ...EMPTY,
    pipeline: {
      ...EMPTY.pipeline,
      totalEstimates: Number(all.total ?? 0),
      awaitingApproval: pending.reduce((total, response) => total + Number(response.total ?? 0), 0),
    },
  };
}

export function usePipelineStats(filter: DateFilter = { period: 'this_week' }) {
  const canViewEstimates = useHasPermission('estimates:view');
  const timeZone = useCompanyTimezone();
  const cacheKey =
    filter.period === 'custom' ? `${filter.startDate}_${filter.endDate}` : filter.period;

  return useApiQuery<PipelineStats>({
    // Estimate mutations already invalidate this prefix.
    queryKey: [...estimateKeys.lists(), 'dashboard-count', cacheKey, timeZone],
    queryFn: () => fetchPipelineStats(filter, new Date(), timeZone),
    enabled: canViewEstimates,
    showErrorToast: false,
    staleTime: 30_000,
  });
}
